import { useEffect, useRef, useState, useCallback, useMemo, type RefObject } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { isAuthNotReadyError, getNotificationAPI } from "@/lib/utils";
import { focusNotificationSession } from "@/lib/notification-deep-link";
import i18n from "@/lib/i18n";
import {
  type StoredSessionState,
  loadAllStoredStates,
  loadSessionState,
  saveSessionState,
} from "@/lib/local-storage-notif";

/** The small portion of a session-list item used by the background poller. */
export interface SessionPollSession {
  id: string;
  last_active_at: string;
  title: string;
  latest_run?: unknown;
  related_tasks?: unknown;
  metadata?: Record<string, unknown>;
  project_id?: string | null;
}

interface EnrichedRunSnapshot {
  run_id: string;
  status: string;
  is_silent: boolean;
}

function hasOwnField(value: object, field: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, field);
}

/** Read additive run data while preserving the old-server fallback contract. */
function readEnrichedLatestRun(
  session: SessionPollSession,
): { supported: boolean; run: EnrichedRunSnapshot | null } {
  if (!hasOwnField(session, "latest_run")) return { supported: false, run: null };
  if (session.latest_run === null) return { supported: true, run: null };
  if (!session.latest_run || typeof session.latest_run !== "object") {
    return { supported: false, run: null };
  }
  const run = session.latest_run as Record<string, unknown>;
  if (typeof run.run_id !== "string" || typeof run.status !== "string") {
    return { supported: false, run: null };
  }
  return {
    supported: true,
    run: {
      run_id: run.run_id,
      status: run.status,
      is_silent: run.is_silent === true,
    },
  };
}

/* ----------------------------------------------------------------- */
/*  Types                                                              */
/* ----------------------------------------------------------------- */

/** Backend RunStatus enum values that mark a finished turn. */
const TERMINAL_RUN_STATUSES = new Set(["completed", "failed", "cancelled"]);

export function isNotifiableTerminalRunTransition(
  previousStatus: string,
  nextStatus: string,
  isSilent: boolean,
): boolean {
  const wasInFlight =
    previousStatus === "running" ||
    previousStatus === "queued" ||
    previousStatus === "paused";
  return wasInFlight && TERMINAL_RUN_STATUSES.has(nextStatus) && !isSilent;
}

/** Notification state for a single session, used by notification provider */
export interface SessionNotificationState {
  sessionId: string;
  /** Whether the session has an unread finished turn since last time the user
   *  viewed it. Set only on a run-status transition into a terminal state,
   *  AND only for sessions created/interacted with on this client. */
  hasUnread: boolean;
  /** Run status snapshot: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' */
  runStatus: string | null;
  /**
   * Whether this session was created or interacted with on this client.
   * Only locally-tracked sessions can show the unread checkmark indicator
   * or fire in-tab browser notifications. Sessions created on other
   * devices rely on the PWA push system for cross-device notifications. */
  locallyTracked: boolean;
}

export interface NotificationAPI {
  /** Whether browser notifications are available */
  isSupported: boolean;
  /** Current permission state: 'default' | 'granted' | 'denied' */
  permission: NotificationPermission;
  /** Whether polling is currently pending */
  loading: boolean;
  /** Request permission from the user */
  enable: () => Promise<void>;
}

export interface NotificationHookReturn {
  notificationState: Map<string, SessionNotificationState>;
  /** Mark a session as read (call after user opens/views it) */
  markSessionRead: (sessionId: string) => void;
  /** The notification API for permission handling and polling state */
  api: NotificationAPI;
  /**
   * Directly set a session's run status (e.g. when a new SSE run starts or
   * completes) so the sidebar indicator updates immediately without waiting
   * for the next background poll tick.
   *
   * Side-effect: persists to localStorage so the poller's skip-conditions
   * also see the updated state, and resets the poll timer to TIER_RUNNING_MS
   * when status is "running" so the poller validates the new state quickly.
   */
  setRunStatus: (sessionId: string, status: string) => void;
}

/* ----------------------------------------------------------------- */
/*  Constants                                                          */
/* ----------------------------------------------------------------- */

/** Maximum age (ms) for a session to be considered "recent" (24 hours). */
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000;

/** Maximum age (ms) for a pending/run-status session to be actionable (12 hours). */
const MAX_PENDING_ACTION_AGE_MS = 12 * 60 * 60 * 1000;

/** Maximum age (ms) for a session to be considered "recent" for per-session polling (12 hours). */
const RECENT_SESSION_AGE_MS = 12 * 60 * 60 * 1000;

/** Adaptive poll tiers – chosen after each poll based on observed session state. */
const TIER_RUNNING_MS = 3_000;     // ≥1 running/submitted run  (visible)
const TIER_ACTIVE_MS  = 20_000;    // ≥1 unread session, none running (visible)
const TIER_QUIET_MS   = 60_000;    // all caught up              (visible)
const TIER_HIDDEN_MS  = 60_000;    // tab hidden, any state
const TIER_STALE_MS   = 5 * 60_000; // nothing recent in ≥12h    (visible)
const TIER_AUTH_NOT_READY_MS = 1_500; // auth still loading; keep retrying quietly

/* ----------------------------------------------------------------- */
/*  Hook                                                              */
/* ----------------------------------------------------------------- */

export function useNotifications(
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
  /**
   * Ref to the currently-active session id. The poller suppresses
   * notifications for this session because the user is already viewing it
   * (the SSE / post-stream reconciliation path renders the response). Passed
   * as a ref to avoid re-creating the entire polling effect on every session
   * switch.
   */
  activeSessionIdRef: RefObject<string | null>,
  /**
   * Optional callback fired when the poller detects that the session list
   * has changed in a way that the sidebar should reflect — e.g. a new session
   * appeared (created on another device), a title changed (auto-titled by
   * the server), or the activity ordering shifted. The caller should use
   * this to trigger a refresh of its session list (useSessions.refresh()).
   *
   * The callback is only fired when something actually changed; it is NOT
   * fired on every poll tick. This avoids redundant refresh requests when
   * the session data is identical to the previous poll.
   *
   * The callback is stored in a ref so it can be called from inside the poll
   * closure without being a dependency of the polling effect (which would
   * tear down and recreate the entire scheduler on every render).
   */
  onSessionsChanged?: () => void,
  /**
   * Durable inbox is the authoritative source for browser/OS delivery once it
   * has connected. Keep this ref out of the polling effect dependencies so a
   * connection-state update cannot tear down the legacy sidebar scheduler.
   */
  durableInboxAvailableRef?: RefObject<boolean>,
  /**
   * A thread can be open alongside its parent, while the parent remains the
   * active top-level session. Treat the thread as active too so a terminal
   * child run does not become unread while its panel is on screen.
   */
  activeChildSessionIdRef?: RefObject<string | null>,
  /**
   * Reconcile a locally tracked child session when its run becomes terminal.
   * Child sessions are absent from the top-level list, so its normal change
   * callback cannot observe title updates for them.
   */
  onUnlistedSessionTerminal?: (sessionId: string) => void,
  /** Receives task enrichment from the same page poll when supported. */
  onSessionEnrichment?: (sessions: SessionPollSession[], fetchStartedAt: number) => void,
): NotificationHookReturn {
  const [notificationState, setNotificationState] = useState<
    Map<string, SessionNotificationState>
  >(new Map());
  const [permission, setPermission] = useState<NotificationPermission>(
    () => getNotificationAPI()?.permission ?? "default",
  );
  const [isSupported] = useState(() => getNotificationAPI() !== undefined && "Notification" in window);
  const [loading, setLoading] = useState(false);

  // Refs for scheduling and visibility without re-renders
  // Read the real page state on the first render. Mobile PWAs can begin
  // bootstrapping while the document is still hidden and become visible only
  // after the initial async work has started.
  const isVisibleRef = useRef(
    typeof document === "undefined" || document.visibilityState === "visible",
  );
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authReadyRef = useRef(false);
  // Stored run state survives page/PWA restarts. The first successful poll
  // therefore establishes a baseline only: a prior "running" value and a
  // now-terminal server value describe work that happened while the app was
  // closed, not a new foreground notification event.
  const hasEstablishedInitialBaselineRef = useRef(false);
  // Holds the current poll() closure so setRunStatus can reschedule it.
  const pollRef = useRef<(() => Promise<void>) | null>(null);
  // Guard to prevent overlapping poll() executions.  Without this, an
  // orphaned setTimeout (see schedulePollRef below) could fire while a
  // poll is still in-flight, creating two concurrent self-scheduling
  // loops that each overwrite pollTimeoutRef — the classic multiplier.
  const pollInFlightRef = useRef(false);
  // Exposes scheduleNextPoll() to setRunStatus (which lives outside the
  // polling effect).  scheduleNextPoll atomically clears any existing
  // timeout before setting a new one, so no setTimeout is ever orphaned.
  const schedulePollRef = useRef<((delay: number) => void) | null>(null);
  useRefreshOnReconnect(() => schedulePollRef.current?.(0));

  // ── Session-list change detection ──────────────────────────────────────
  // The poller fetches /api/sessions on every tick.  We compare the result
  // against a snapshot of the last poll to detect changes that the sidebar
  // should reflect (new sessions, title changes, activity reordering).
  // When a change is detected, onSessionsChanged is called so the caller
  // can refresh its session list.  The callback and snapshot are stored in
  // refs so they don't re-create the polling effect.
  const onSessionsChangedRef = useRef<(() => void) | null>(null);
  onSessionsChangedRef.current = onSessionsChanged ?? null;
  const onUnlistedSessionTerminalRef = useRef<((sessionId: string) => void) | null>(null);
  onUnlistedSessionTerminalRef.current = onUnlistedSessionTerminal ?? null;
  const onSessionEnrichmentRef = useRef<((sessions: SessionPollSession[], fetchStartedAt: number) => void) | null>(null);
  onSessionEnrichmentRef.current = onSessionEnrichment ?? null;
  const reconciledTerminalRunsRef = useRef<Set<string>>(new Set());
  // Snapshot of the last poll's session list: Map<id, { title, lastActiveAt }>
  const lastSessionsSnapshotRef = useRef<Map<string, { title: string; lastActiveAt: string }>>(new Map());

  const markSessionRead = useCallback((sessionId: string) => {
    // 1. Persist the read timestamp to localStorage so the next poll
    //    doesn't fire a browser notification for already-seen messages.
    const existing = loadAllStoredStates().get(sessionId);
    // Preserve the current runStatus so that opening a session while the agent
    // is still running doesn't immediately hide the spinner. The next poll
    // will reconcile the status to whatever the server reports.
    const preservedRunStatus = existing?.lastKnownRunStatus ?? "idle";
    const updated: StoredSessionState = {
      lastReadTimestamp: Date.now(),
      lastKnownRunStatus: preservedRunStatus,
      lastKnownRunId: existing?.lastKnownRunId ?? null,
      lastNotifiedRunId: existing?.lastNotifiedRunId ?? null,
      lastKnownMessageId: existing?.lastKnownMessageId ?? null,
      lastCheckedActiveAt: existing?.lastCheckedActiveAt ?? null,
      lastActiveAt: existing?.lastActiveAt ?? null,
      hasUnread: false,
      locallyTracked: existing?.locallyTracked ?? false,
      lastCheckedAt: existing?.lastCheckedAt ?? null,
    };
    saveSessionState(sessionId, updated);

    // 2. Clear the in-memory unread indicator immediately (no need to wait
    //    for the next poll tick).
    setNotificationState((prev) => {
      const entry = prev.get(sessionId);
      if (!entry?.hasUnread) return prev;
      const next = new Map(prev);
      next.set(sessionId, { ...entry, hasUnread: false });
      return next;
    });
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) return;
    const NotificationAPI = getNotificationAPI();
    if (!NotificationAPI) return;
    try {
      const result = await NotificationAPI.requestPermission();
      setPermission(result);
    } catch {
      // permission request failed (user dismissed, etc.)
    }
  }, [isSupported]);

  /**
   * Immediately sync a session's run status into both localStorage and the
   * in-memory notification state, bypassing the background poll cycle.
   *
   * When status is "running" or "queued" the background poll timer is also
   * reset to TIER_RUNNING_MS so the next verification happens within 3 s.
   *
   * NOTE: useSessionMessageFeed calls this from each live/recovered feed when
   * the user is actively viewing a session. It intentionally does NOT set
   * hasUnread — the user is already looking at the session, so there is no
   * need to flag it as unread or fire a browser notification. The background
   * poller applies the same suppression via the active-session refs.
   *
   * SIDE-EFFECT: marks the session as locallyTracked. setRunStatus is called
   * from the SSE path (user sent a message on this client), so this is the
   * definitive signal that the session is being interacted with here. Only
   * locally-tracked sessions get hasUnread checkmark indicators and in-tab
   * browser notifications from the polling system. Sessions created on other
   * devices rely on the PWA push system for cross-device notifications.
   */
  const setRunStatus = useCallback((sessionId: string, status: string) => {
    // 1. Persist to localStorage so the poller's skip-conditions see it.
    const existing = loadSessionState(sessionId);
    const isInFlight = status === "running" || status === "queued" || status === "paused";
    const now = new Date().toISOString();
    const updated: StoredSessionState = {
      lastReadTimestamp: existing?.lastReadTimestamp ?? 0,
      lastKnownRunStatus: status,
      lastKnownRunId: existing?.lastKnownRunId ?? null,
      lastNotifiedRunId: existing?.lastNotifiedRunId ?? null,
      lastKnownMessageId: existing?.lastKnownMessageId ?? null,
      lastCheckedActiveAt: existing?.lastCheckedActiveAt ?? null,
      // Child sessions are absent from /api/sessions. Record their latest
      // local activity so the supplemental in-flight poll below can apply
      // the same recency limits as top-level sessions.
      lastActiveAt: isInFlight ? now : (existing?.lastActiveAt ?? now),
      hasUnread: existing?.hasUnread ?? false,
      // Mark as locally-tracked — this function is called from the SSE path
      // (user sent a message on this client), which is the definitive signal
      // that the session is being interacted with here.
      locallyTracked: true,
      lastCheckedAt: existing?.lastCheckedAt ?? null,
    };
    saveSessionState(sessionId, updated);

    // 2. Update in-memory state immediately for instant sidebar feedback.
    setNotificationState((prev) => {
      const next = new Map(prev);
      const entry = next.get(sessionId) ?? { sessionId, hasUnread: false, runStatus: null, locallyTracked: false };
      if (entry.runStatus === status) return prev; // bail out if nothing changed
      next.set(sessionId, { ...entry, runStatus: status, locallyTracked: true });
      return next;
    });

    // 3. When a run starts or pauses, kick the background poller into fast
    //    mode so it confirms the state and picks up transitions (resume,
    //    completion) within 3 s.
    //    Use schedulePollRef (the atomic clear-then-set helper) instead of
    //    directly manipulating pollTimeoutRef — the old code orphaned the
    //    in-flight poll()'s self-scheduled timeout, creating a second
    //    concurrent poll loop that permanently multiplied API requests.
    if ((status === "running" || status === "queued" || status === "paused") && schedulePollRef.current) {
      schedulePollRef.current(TIER_RUNNING_MS);
    }
  }, []);

  // ── Visibility-aware polling + cleanup ──
  useEffect(() => {
    // Prevent an in-flight poll from a torn-down effect (notably React
    // StrictMode's development remount) from writing state or flipping the
    // first-poll baseline after its replacement has started.
    let cancelled = false;

    /** Decide when to poll next based on the most interesting session state. */
    function computeNextDelay(stored: Map<string, StoredSessionState>): number {
      if (!isVisibleRef.current) return TIER_HIDDEN_MS;

      let anyRunning = false;
      let anyRecentActivity = false;
      for (const s of stored.values()) {
        const lastActive = s.lastActiveAt ? new Date(s.lastActiveAt).getTime() : 0;
        const hasRecentPendingRun =
          (s.lastKnownRunStatus === "running" ||
            s.lastKnownRunStatus === "queued" ||
            s.lastKnownRunStatus === "paused") &&
          lastActive > 0 &&
          Date.now() - lastActive <= MAX_PENDING_ACTION_AGE_MS;
        if (hasRecentPendingRun) {
          anyRunning = true;
        }
        if (Date.now() - lastActive < RECENT_SESSION_AGE_MS) {
          anyRecentActivity = true;
        }
        // unread tier is checked after running; if there is also a running
        // run the running tier will win because of the earlier return.
      }
      if (anyRunning) return TIER_RUNNING_MS;

      // Recent sessions with unread messages are still moderately interesting.
      if (anyRecentActivity) {
        for (const s of stored.values()) {
          if (s.hasUnread) return TIER_ACTIVE_MS;
        }
      }

      return anyRecentActivity ? TIER_QUIET_MS : TIER_STALE_MS;
    }

    function handleVisibility() {
      isVisibleRef.current = document.visibilityState === "visible";
      if (isVisibleRef.current) {
        // Immediate catch-up via the safe schedulePoll path.  This atomically
        // clears any pending timeout before triggering the poll, so we never
        // orphan a timeout that the in-flight poll() would later overwrite.
        schedulePollRef.current?.(0);
      } else {
        // Tab hidden — park on the slow interval.
        schedulePollRef.current?.(TIER_HIDDEN_MS);
      }
    }

    // ── Atomic scheduling helper ──
    // Always clears any existing timeout before setting a new one, so no
    // setTimeout is ever orphaned (which was the root cause of the request
    // multiplication: a setRunStatus or visibility handler would overwrite
    // pollTimeoutRef while a poll() was in-flight, orphaning the timeout
    // that poll() would later self-schedule — creating 2+ concurrent loops).
    function scheduleNextPoll(delay: number) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      const pollFn = pollRef.current;
      if (!pollFn) return;
      pollTimeoutRef.current = setTimeout(() => void pollFn(), Math.max(0, delay));
    }

    // Expose the scheduler so setRunStatus (outside this effect) can safely
    // reschedule without orphaning.
    schedulePollRef.current = scheduleNextPoll;

    // Poll single tick
    async function poll() {
      // Guard: never run two polls concurrently.  If scheduleNextPoll fired
      // (from setRunStatus or handleVisibility) while a previous poll was
      // still awaiting its fetch, the guard makes the second invocation a
      // no-op instead of starting a second self-scheduling loop.
      if (cancelled || pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      const isInitialBaselineAttempt = !hasEstablishedInitialBaselineRef.current;
      if (isInitialBaselineAttempt) setLoading(true);
      try {
        const fetchStartedAt = Date.now();
        const res = await fetchWithAuth("/api/sessions?limit=20", { headers: {} });
        if (cancelled) return;
        if (!res.ok) return;
        // Once we successfully talk to the backend, auth is ready; we can drop
        // the auth-not-ready short-circuit on future ticks.
        authReadyRef.current = true;
        const data: { sessions?: SessionPollSession[] } = await res.json();
        if (cancelled) return;
        const sessions = data.sessions || [];
        // Keep this stable for the entire pass. A single first poll may
        // reconcile several persisted sessions, and none of those stale
        // transitions should become OS notifications.
        const isInitialBaselinePoll = !hasEstablishedInitialBaselineRef.current;

        // ── Detect session-list changes for sidebar refresh ──────────────
        // Compare the current session list against the last poll's snapshot.
        // If any session was added, removed, renamed, or reordered by
        // last_active_at, fire onSessionsChanged so the caller can refresh
        // the sidebar.  This ensures the sidebar reflects sessions created on
        // other devices, auto-generated titles, and activity shifts without
        // requiring a manual action.
        //
        // The comparison is cheap (20 items × string equality) and only
        // triggers a refresh when something actually changed, so it doesn't
        // add redundant requests on quiet ticks.
        {
          const prev = lastSessionsSnapshotRef.current;
          let changed = false;

          // Quick size check — different count means something was added or
          // removed.
          if (prev.size !== sessions.length) {
            changed = true;
          } else {
            // Same count — compare each session's id, title, and position
            // (order reflects last_active_at descending from the API).
            for (let i = 0; i < sessions.length; i++) {
              const s = sessions[i];
              const prevEntry = prev.get(s.id);
              // New session ID not in previous snapshot
              if (!prevEntry) { changed = true; break; }
              // Title changed (e.g. auto-generated by the server)
              if (prevEntry.title !== s.title) { changed = true; break; }
            }

            // If no individual changes found, check for reordering by
            // comparing the ordered list of IDs.  A different order means
            // last_active_at shifted (another device sent a message, etc).
            if (!changed) {
              const prevIds = Array.from(prev.keys());
              const currIds = sessions.map(s => s.id);
              for (let i = 0; i < currIds.length; i++) {
                if (prevIds[i] !== currIds[i]) { changed = true; break; }
              }
            }
          }

          if (changed) {
            // Update the snapshot for next tick's comparison.
            const newSnapshot = new Map<string, { title: string; lastActiveAt: string }>();
            for (const s of sessions) {
              newSnapshot.set(s.id, { title: s.title, lastActiveAt: s.last_active_at });
            }
            lastSessionsSnapshotRef.current = newSnapshot;
            // Fire the callback (if registered) so the caller can refresh.
            onSessionsChangedRef.current?.();
          }
        }

        // Enrichment is a capability signal. An independently deployed old
        // server omits the key and simply retains its existing fallback.
        if (sessions.some((session) => hasOwnField(session, "related_tasks"))) {
          onSessionEnrichmentRef.current?.(sessions, fetchStartedAt);
        }

        // Load stored state from localStorage
        const stored = loadAllStoredStates();

        // Only check sessions that have been active in the last 24 hours
        const cutoffTime = Date.now() - MAX_SESSION_AGE_MS;
        const recentCutoffTime = Date.now() - RECENT_SESSION_AGE_MS;

        // The top-level endpoint intentionally excludes threads. A child
        // session that starts a run through useSessionMessageFeed still calls
        // setRunStatus(), so include such locally tracked in-flight sessions
        // here even when they are absent from the list response. This keeps
        // their terminal run-status transitions and feed reconciliation on
        // the same path as normal sessions without recursively polling every
        // child session in the account.
        const listedSessionIds = new Set(sessions.map((session) => session.id));
        const missingInFlightSessions = Array.from(stored.entries()).flatMap(
          ([id, state]) => {
            const isInFlight =
              state.lastKnownRunStatus === "running" ||
              state.lastKnownRunStatus === "queued" ||
              state.lastKnownRunStatus === "paused";
            if (listedSessionIds.has(id) || !state.locallyTracked || !isInFlight) {
              return [];
            }

            return [{
              id,
              // The thread title is not present in the top-level response.
              // It is only used if a background browser notification is
              // needed; the panel itself already renders its real title.
              title: i18n.t("chat:threads.thread"),
              last_active_at: state.lastActiveAt ?? new Date().toISOString(),
            }];
          },
        );

        for (const session of [...sessions, ...missingInFlightSessions]) {
          const lastActive = new Date(session.last_active_at).getTime();

          // Skip sessions inactive for more than 24h
          if (lastActive < cutoffTime) continue;

          const known = stored.get(session.id);

          // Treat a session as "new" only if it has a stored lastActiveAt (i.e.
          // the user has opened/seen it before) or it already has state from this
          // notification system. Without prior interaction, never flag it as
          // having unread/pending messages.
          const isKnown = Boolean(
            known &&
              (known.lastActiveAt != null ||
                known.lastReadTimestamp != null ||
                known.lastKnownMessageId != null ||
                known.lastKnownRunId != null),
          );
          if (!isKnown) {
            // A list enrichment is safe to render even for sessions first
            // discovered on another device. It does not make them locally
            // tracked or create notifications; it simply lets the sidebar
            // show a genuinely live run without an extra request.
            const initialEnrichedRun = readEnrichedLatestRun(session);
            const initialRun = initialEnrichedRun.supported
              ? initialEnrichedRun.run
              : null;
            // Record that we saw the session, but do not flag as pending yet.
            // locallyTracked defaults to false — this session was not created
            // on this client (we discovered it via the sessions list poll, not
            // via setRunStatus from the SSE path). It will only become
            // locallyTracked if the user later sends a message from here.
            const seenState: StoredSessionState = {
              lastReadTimestamp: known?.lastReadTimestamp ?? 0,
              lastKnownRunStatus: initialRun?.status ?? "idle",
              lastKnownRunId: initialRun?.run_id ?? known?.lastKnownRunId ?? null,
              lastNotifiedRunId: known?.lastNotifiedRunId ?? null,
              lastKnownMessageId: known?.lastKnownMessageId ?? null,
              lastCheckedActiveAt: session.last_active_at,
              lastActiveAt: session.last_active_at,
              hasUnread: false,
              locallyTracked: known?.locallyTracked ?? false,
              lastCheckedAt: Date.now(),
            };
            saveSessionState(session.id, seenState);
            stored.set(session.id, seenState);
            continue;
          }

          // Skip sessions the user has already read (lastRead > lastActive),
          // UNLESS the session has a known in-flight run.  When the user is
          // actively viewing a session, `markSessionRead` keeps advancing
          // `lastReadTimestamp`, and the backend only updates `last_active_at`
          // at run START — not when the run completes.  Without this exception,
          // the poller would never re-poll to discover the run finished, so the
          // spinner in the sidebar would stay forever.
          const lastRead = known?.lastReadTimestamp ?? 0;
          const isKnownRunningEarly =
            known?.lastKnownRunStatus === "running" ||
            known?.lastKnownRunStatus === "queued" ||
            known?.lastKnownRunStatus === "paused";
          if (lastActive <= lastRead && known && !isKnownRunningEarly) continue;

          // Sessions with no recent activity (12h) are throttled to ~5 min.
          const isRecent = lastActive >= recentCutoffTime;
          // "queued" is the backend's RunStatus for a not-yet-started run;
          // "running" is the active state.  (The old code checked "submitted",
          // which is the AI SDK's ChatStatus enum value, not the backend's
          // RunStatus — those values never match the runs API response.)
          const isKnownRunning =
            known?.lastKnownRunStatus === "running" ||
            known?.lastKnownRunStatus === "queued" ||
            known?.lastKnownRunStatus === "paused";
          const lastCheckedAt = known?.lastCheckedAt ?? 0;
          const staleCheckDue = Date.now() - lastCheckedAt >= TIER_STALE_MS;
          if (!isRecent && !isKnownRunning && !staleCheckDue) continue;

          // Skip sessions we have already checked at this exact activity level,
          // UNLESS the session has a known in-flight run — last_active_at may
          // not advance until the run completes, so we must keep polling it to
          // detect the terminal transition.
          if (
            !isKnownRunning &&
            known?.lastCheckedActiveAt === session.last_active_at &&
            known?.lastKnownRunId != null
          ) continue;

          // ── Run-status snapshot ────────────────────────────────────────
          // New servers attach the trustworthy newest run to the list page.
          // Servers without the additive field retain the legacy /runs call.
          let runStatus: string | undefined;
          let runId: string | null = known?.lastKnownRunId ?? null;
          let runIsSilent = false;
          const enrichedRun = readEnrichedLatestRun(session);
          if (enrichedRun.supported) {
            if (enrichedRun.run) {
              runStatus = enrichedRun.run.status;
              runId = enrichedRun.run.run_id;
              runIsSilent = enrichedRun.run.is_silent;
            } else {
              // Preserve only the existing short start-race guard. Beyond it,
              // null is authoritative idle state and clears a zombie spinner.
              const sinceActive = Date.now() - lastActive;
              const RACE_WINDOW_MS = 10_000;
              runStatus = sinceActive < RACE_WINDOW_MS
                ? (known?.lastKnownRunStatus ?? "idle")
                : "idle";
            }
          } else {
            try {
              const runsRes = await fetchWithAuth(
                `/api/sessions/${session.id}/runs?limit=1`,
                { headers: {} },
              );
              if (!runsRes.ok) continue;
              interface RunItem {
                run_id: string;
                status: string;
                is_silent?: boolean;
              }
              const runsData = await runsRes.json() as { runs?: RunItem[] };
              if (cancelled) return;
              const runs = Array.isArray(runsData.runs) ? runsData.runs : [];
              if (runs.length > 0) {
                runStatus = runs[0].status;
                runId = runs[0].run_id;
                runIsSilent = runs[0].is_silent === true;
              } else {
                const sinceActive = Date.now() - lastActive;
                const RACE_WINDOW_MS = 10_000;
                runStatus = sinceActive < RACE_WINDOW_MS
                  ? (known?.lastKnownRunStatus ?? "idle")
                  : "idle";
              }
            } catch {
              // Keep this session's stored state untouched until runs load.
              continue;
            }
          }

          const updatedStatus = runStatus ?? known?.lastKnownRunStatus ?? "idle";

          // A pending run status is not actionable if the thread is stale.
          // If the last activity was more than 12 hours ago, treat the session
          // as idle for notification purposes regardless of backend status.
          const statusIsPending =
            updatedStatus === "running" ||
            updatedStatus === "queued" ||
            updatedStatus === "paused";
          const isStale = Date.now() - lastActive > MAX_PENDING_ACTION_AGE_MS;
          const effectiveStatus =
            statusIsPending && isStale ? "idle" : updatedStatus;

          // ── Detect terminal run-status transition ──
          // A notification is raised only when ALL of the following hold:
          //  • the run transitions from an in-flight state (queued|running)
          //    to a terminal state (completed|failed|cancelled)
          //  • the user was not viewing the session (isActiveSession is false)
          //  • we haven't already notified for this run_id
          //  • the session is locallyTracked — created/interacted with on this
          //    client (via setRunStatus from the SSE path). Sessions created on
          //    other devices are NOT notified by the polling system; they rely
          //    on the PWA push system for cross-device notifications, since we
          //    cannot reliably track read state across devices.
          const prevStatus = known?.lastKnownRunStatus ?? "idle";
          // Silent terminal runs still clear the local in-flight state, but
          // they are deliberately excluded from every user-facing terminal
          // side effect (unread, browser notification, and list reconciliation).
          const isNotifiableTransition = isNotifiableTerminalRunTransition(
            prevStatus,
            effectiveStatus,
            runIsSilent,
          );
          // When runId is null (no runs returned), treat as already notified
          // to avoid spurious notifications on idle sessions.
          const alreadyNotified =
            runId != null && known?.lastNotifiedRunId === runId;
          const isActiveSession =
            activeSessionIdRef.current === session.id ||
            activeChildSessionIdRef?.current === session.id;
          const isLocallyTracked = known?.locallyTracked ?? false;

          if (
            isNotifiableTransition
            && isLocallyTracked
            && !listedSessionIds.has(session.id)
          ) {
            const reconciliationKey = `${session.id}:${runId ?? effectiveStatus}`;
            if (!reconciledTerminalRunsRef.current.has(reconciliationKey)) {
              reconciledTerminalRunsRef.current.add(reconciliationKey);
              onUnlistedSessionTerminalRef.current?.(session.id);
            }
          }

          const shouldNotify =
            !isInitialBaselinePoll &&
            isNotifiableTransition &&
            !alreadyNotified &&
            !isActiveSession &&
            isLocallyTracked;

          // Preserve existing unread state unless we're raising a new
          // notification. This keeps the green check visible until the user
          // opens the session (markSessionRead clears it).
          // For non-locally-tracked sessions, hasUnread is never set to true
          // — the PWA push system handles notifications for sessions created
          // on other devices.
          const newHasUnread =
            shouldNotify && isLocallyTracked
              ? true
              : (known?.hasUnread ?? false);

          // ── Fire browser popup (only on terminal transition, hidden tab,
          //     locally-tracked session) ──
          // In-tab browser notifications are only for sessions created/interacted
          // with on this client. Once the durable inbox has connected, it is
          // also the sole browser/OS delivery path so terminal runs do not
          // produce competing page and service-worker notifications. Legacy
          // state remains intact for sidebar indicators.
          if (
            shouldNotify &&
            isLocallyTracked &&
            !durableInboxAvailableRef?.current &&
            document.visibilityState === "hidden" &&
            getNotificationAPI()?.permission === "granted"
          ) {
            const body =
              effectiveStatus === "failed"
                ? `Agent failed in "${session.title}"`
                : effectiveStatus === "cancelled"
                  ? `Run cancelled in "${session.title}"`
                  : `Agent finished in "${session.title}"`;
            try {
              const NotificationAPI = getNotificationAPI();
              if (NotificationAPI) {
                const notification = new NotificationAPI(import.meta.env.APP_NAME ?? "Layer Nine", {
                  body,
                  icon: "/favicon-dark.ico",
                  tag: session.id,
                  data: {
                    session_id: session.id,
                    run_id: runId,
                    status: effectiveStatus,
                  },
                });
                notification.onclick = () => {
                  notification.close();
                  focusNotificationSession(notification.data);
                };
              }
            } catch {
              // ignore
            }
          }

          // ── Persist state (single write per session per tick) ──
          const newState: StoredSessionState = {
            lastReadTimestamp: known?.lastReadTimestamp ?? 0,
            lastKnownRunStatus: effectiveStatus,
            lastKnownRunId: runId,
            lastNotifiedRunId: shouldNotify
              ? runId
              : (known?.lastNotifiedRunId ?? null),
            lastKnownMessageId: known?.lastKnownMessageId ?? null,
            lastCheckedActiveAt: session.last_active_at,
            lastActiveAt: session.last_active_at,
            hasUnread: newHasUnread,
            locallyTracked: known?.locallyTracked ?? false,
            lastCheckedAt: Date.now(),
          };
          saveSessionState(session.id, newState);
          stored.set(session.id, newState);
        }

        // Only a completed successful pass establishes the baseline. Failed
        // auth/network attempts must not suppress the first real reconciliation
        // when the app becomes ready.
        hasEstablishedInitialBaselineRef.current = true;

        // Build React state map from the now-updated stored map.
        // Use functional setState and bail out if nothing changed to avoid
        // triggering unnecessary re-renders every poll tick.
        setNotificationState((prev) => {
          const uiState = new Map<string, SessionNotificationState>();
          for (const [id, s] of stored) {
            uiState.set(id, {
              sessionId: id,
              hasUnread: s.hasUnread ?? false,
              runStatus: s.lastKnownRunStatus ?? null,
              locallyTracked: s.locallyTracked ?? false,
            });
          }

          // Bail out (return same reference) if content is identical.
          if (prev.size === uiState.size) {
            let changed = false;
            for (const [id, s] of uiState) {
              const p = prev.get(id);
              if (!p || p.hasUnread !== s.hasUnread || p.runStatus !== s.runStatus || p.locallyTracked !== s.locallyTracked) {
                changed = true;
                break;
              }
            }
            if (!changed) return prev;
          }

          return uiState;
        });
        // Schedule the next poll based on the observed state.
        scheduleNextPoll(computeNextDelay(stored));
      } catch (err) {
        if (cancelled) return;
        if (isAuthNotReadyError(err)) {
          // AuthKit is still initializing. Don't log an error; just wait and
          // retry shortly. The token is fetched fresh by fetchWithAuth, so the
          // next tick will succeed once the session is ready.
          scheduleNextPoll(TIER_AUTH_NOT_READY_MS);
          return;
        }
        console.error("[useNotifications] Poll error:", err);
        // Back off to the stale interval on failure — don't hammer a broken endpoint.
        scheduleNextPoll(TIER_STALE_MS);
      } finally {
        // A replacement effect owns these shared refs after cleanup. Do not
        // let an old cancelled poll clear its in-flight/loading state and
        // accidentally permit another overlapping poll.
        if (!cancelled) {
          pollInFlightRef.current = false;
          if (isInitialBaselineAttempt) setLoading(false);
        }
      }
    }

    // Synchronize before the immediate poll. The visibility handler is also
    // registered before it, so a mobile PWA's first focus transition cannot
    // leave the scheduler believing the document is visible when it is not.
    isVisibleRef.current = document.visibilityState === "visible";
    document.addEventListener("visibilitychange", handleVisibility);

    // Expose poll() and scheduleNextPoll() so setRunStatus (outside this
    // effect) can safely reschedule without orphaning timeouts.
    pollRef.current = poll;
    schedulePollRef.current = scheduleNextPoll;

    // Kick off the first poll immediately; it self-schedules all subsequent ones.
    void poll();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      pollInFlightRef.current = false;
      // Clear the refs so setRunStatus can't trigger a poll after teardown.
      pollRef.current = null;
      schedulePollRef.current = null;
    };
  }, [fetchWithAuth]);

  // Memoize the api object so its identity is stable across renders (when
  // its deps don't change).  Without this, every render of useNotifications
  // produces a new api object, which breaks the useMemo in NotificationProvider
  // and re-renders every context consumer on every poll tick.
  const api = useMemo<NotificationAPI>(() => ({
    isSupported,
    permission,
    loading,
    enable: requestPermission,
  }), [isSupported, permission, loading, requestPermission]);

  return {
    notificationState,
    markSessionRead,
    setRunStatus,
    api,
  };
}
